import { RotateCcw } from 'lucide-react';
import type { AnalyticsDimensions, JourneyStepDto } from '@ederest/contracts';
import { Button } from '../../shared/ui/Button.js';
import { PERIOD_PRESETS, type useAnalyticsFilters } from '../../shared/api/useAnalyticsFilters.js';
import '../../shared/ui/filters.css';

type Filters = ReturnType<typeof useAnalyticsFilters>;

interface FilterBarProps {
  filters: Filters;
  journeys: ReadonlyArray<{ id: string; name: string }>;
  steps: ReadonlyArray<JourneyStepDto>;
  fieldKeys: readonly string[];
  dimensions: AnalyticsDimensions | undefined;
}

const VIEWPORT_LABELS: Record<string, string> = {
  mobile: 'Téléphone',
  tablet: 'Tablette',
  desktop: 'Ordinateur',
};
const LANGUAGE_LABELS: Record<string, string> = { fr: 'Français', ar: 'Arabe', zgh: 'Amazighe' };

/**
 * Combinable filters of functional §5.4: period, journey, step, field, language,
 * device, applied guidance version. Options come from the measured data.
 */
export function FilterBar({ filters, journeys, steps, fieldKeys, dimensions }: FilterBarProps) {
  const { filters: value, set, setPeriod, reset, activeCount, activePreset } = filters;

  return (
    <div className="filters" role="group" aria-label="Filtres du tableau de bord">
      <div className="filters__presets" role="group" aria-label="Période">
        {PERIOD_PRESETS.map((days) => (
          <button
            key={days}
            type="button"
            className={`filters__preset ${activePreset === days ? 'filters__preset--active' : ''}`}
            aria-pressed={activePreset === days}
            onClick={() => setPeriod(days)}
          >
            {days} jours
          </button>
        ))}
      </div>

      <label className="filters__field">
        <span>Parcours</span>
        <select
          aria-label="Filtrer par parcours"
          value={value.journeyId ?? ''}
          onChange={(e) => set({ journeyId: e.target.value })}
        >
          <option value="">Tous les parcours</option>
          {journeys.map((journey) => (
            <option key={journey.id} value={journey.id}>
              {journey.name}
            </option>
          ))}
        </select>
      </label>

      <label className="filters__field">
        <span>Sous-étape</span>
        <select
          aria-label="Filtrer par sous-étape"
          value={value.stepId ?? ''}
          disabled={!value.journeyId}
          onChange={(e) => set({ stepId: e.target.value })}
        >
          <option value="">{value.journeyId ? 'Toutes les étapes' : 'Choisir un parcours'}</option>
          {steps.map((step) => (
            <option key={step.id} value={step.id}>
              {step.position}. {step.name}
            </option>
          ))}
        </select>
      </label>

      <label className="filters__field">
        <span>Champ</span>
        <select
          aria-label="Filtrer par champ"
          value={value.fieldKey ?? ''}
          onChange={(e) => set({ fieldKey: e.target.value })}
        >
          <option value="">Tous les champs</option>
          {fieldKeys.map((fieldKey) => (
            <option key={fieldKey} value={fieldKey}>
              {fieldKey}
            </option>
          ))}
        </select>
      </label>

      <label className="filters__field">
        <span>Langue</span>
        <select
          aria-label="Filtrer par langue"
          value={value.language ?? ''}
          onChange={(e) => set({ language: e.target.value })}
        >
          <option value="">Toutes les langues</option>
          {(dimensions?.languages ?? []).map((language) => (
            <option key={language} value={language}>
              {LANGUAGE_LABELS[language] ?? language}
            </option>
          ))}
        </select>
      </label>

      <label className="filters__field">
        <span>Appareil</span>
        <select
          aria-label="Filtrer par appareil"
          value={value.viewport ?? ''}
          onChange={(e) => set({ viewport: e.target.value })}
        >
          <option value="">Tous les appareils</option>
          {(dimensions?.viewports ?? []).map((viewport) => (
            <option key={viewport} value={viewport}>
              {VIEWPORT_LABELS[viewport] ?? viewport}
            </option>
          ))}
        </select>
      </label>

      <label className="filters__field">
        <span>Version de guidance</span>
        <select
          aria-label="Filtrer par version de guidance"
          value={value.configurationVersion !== undefined ? String(value.configurationVersion) : ''}
          onChange={(e) => set({ configurationVersion: e.target.value })}
        >
          <option value="">Toutes les versions</option>
          {(dimensions?.configurationVersions ?? []).map((version) => (
            <option key={version} value={version}>
              Version {version}
            </option>
          ))}
        </select>
      </label>

      {activeCount > 0 && (
        <Button variant="ghost" icon={<RotateCcw />} onClick={reset} aria-label="Réinitialiser les filtres">
          Réinitialiser
        </Button>
      )}
    </div>
  );
}
